import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { base44 } from '@/api/base44Client';

const CartContext = createContext(null);

const GUEST_CART_KEY = 'mr_guest_cart';

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [cartId, setCartId] = useState(null);
  const [userEmail, setUserEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fulfilmentMethod, setFulfilmentMethod] = useState('delivery');

  useEffect(() => {
    initCart();
  }, []);

  const initCart = async () => {
    setLoading(true);
    const authed = await base44.auth.isAuthenticated();
    if (authed) {
      const user = await base44.auth.me();
      setUserEmail(user.email);
      await loadUserCart(user.email);
    } else {
      const stored = localStorage.getItem(GUEST_CART_KEY);
      if (stored) setItems(JSON.parse(stored));
    }
    setLoading(false);
  };

  const mapCartItem = (ci) => ({
    id: ci.id, // cartItemId alias
    cartItemId: ci.id,
    type: ci.type || 'product',
    // product fields
    product_id: ci.product_id,
    product_name: ci.product_name,
    product_image_url: ci.product_image_url,
    // booking fields
    experience_type: ci.experience_type,
    rig_type: ci.rig_type,
    date: ci.date,
    time_slot: ci.time_slot,
    duration: ci.duration,
    customer_name: ci.customer_name,
    customer_email: ci.customer_email,
    customer_phone: ci.customer_phone,
    notes: ci.notes,
    referral_source: ci.referral_source,
    // gift card fields
    gift_card_mode: ci.gift_card_mode,
    delivery_method: ci.delivery_method,
    send_to_self: ci.send_to_self,
    recipient_name: ci.recipient_name,
    recipient_email: ci.recipient_email,
    recipient_address_line1: ci.recipient_address_line1,
    recipient_address_line2: ci.recipient_address_line2,
    recipient_city: ci.recipient_city,
    recipient_postcode: ci.recipient_postcode,
    gift_message: ci.gift_message,
    // shared
    price: ci.price,
    quantity: ci.quantity,
  });

  const loadUserCart = async (email) => {
    const carts = await base44.entities.Cart.filter({ user_email: email, status: 'active' });
    let cart = carts[0];
    if (!cart) {
      cart = await base44.entities.Cart.create({ user_email: email, status: 'active' });
    }
    setCartId(cart.id);

    const cartItems = await base44.entities.CartItem.filter({ cart_id: cart.id });

    // Enrich product items with the latest product image if missing
    for (const ci of cartItems) {
      if (ci.type === 'product' && ci.product_id && !ci.product_image_url) {
        try {
          const p = await base44.entities.Product.get(ci.product_id);
          if (p && p.image_url) ci.product_image_url = p.image_url;
        } catch {}
      }
    }

    // Merge guest cart (product items only)
    const guestRaw = localStorage.getItem(GUEST_CART_KEY);
    if (guestRaw) {
      const guestItems = JSON.parse(guestRaw);
      for (const gi of guestItems) {
        if (gi.type === 'booking') continue; // don't merge guest bookings
        const existing = cartItems.find(ci => ci.product_id === gi.product_id);
        if (existing) {
          await base44.entities.CartItem.update(existing.id, { quantity: existing.quantity + gi.quantity });
          existing.quantity += gi.quantity;
        } else {
          const created = await base44.entities.CartItem.create({ ...gi, cart_id: cart.id });
          cartItems.push(created);
        }
      }
      localStorage.removeItem(GUEST_CART_KEY);
    }

    setItems(cartItems.map(mapCartItem));
  };

  const saveGuestCart = (newItems) => {
    localStorage.setItem(GUEST_CART_KEY, JSON.stringify(newItems));
  };

  // Add a product to cart
  const addToCart = useCallback(async (product, qty = 1) => {
    const existing = items.find(i => i.type !== 'booking' && i.product_id === product.id);

    if (cartId) {
      if (existing) {
        const newQty = existing.quantity + qty;
        await base44.entities.CartItem.update(existing.cartItemId, { quantity: newQty });
        setItems(prev => prev.map(i => i.product_id === product.id ? { ...i, quantity: newQty } : i));
      } else {
        const created = await base44.entities.CartItem.create({
          cart_id: cartId,
          type: 'product',
          product_id: product.id,
          product_name: product.name,
          product_image_url: product.image_url || '',
          price: product.price,
          quantity: qty,
        });
        setItems(prev => [...prev, mapCartItem(created)]);
      }
    } else {
      let newItems;
      if (existing) {
        newItems = items.map(i => i.product_id === product.id ? { ...i, quantity: i.quantity + qty } : i);
      } else {
        newItems = [...items, {
          type: 'product',
          product_id: product.id,
          product_name: product.name,
          product_image_url: product.image_url || '',
          price: product.price,
          quantity: qty,
        }];
      }
      setItems(newItems);
      saveGuestCart(newItems);
    }
  }, [items, cartId]);

  // Add a booking session to cart (always requires login for now)
  const addBookingToCart = useCallback(async (bookingData) => {
    const newItemData = {
      cart_id: cartId,
      type: 'booking',
      experience_type: bookingData.experience_type,
      rig_type: bookingData.rig_type,
      date: bookingData.date,
      time_slot: bookingData.time_slot,
      duration: bookingData.duration || 1,
      customer_name: bookingData.customer_name,
      customer_email: bookingData.customer_email,
      customer_phone: bookingData.customer_phone || '',
      notes: bookingData.notes || '',
      referral_source: bookingData.referral_source || '',
      price: bookingData.price,
      quantity: 1,
      product_name: bookingData.experience_name, // display name for summary
    };

    if (cartId) {
      const created = await base44.entities.CartItem.create(newItemData);
      setItems(prev => [...prev, mapCartItem(created)]);
    } else {
      const newItems = [...items, newItemData];
      setItems(newItems);
      saveGuestCart(newItems);
    }
  }, [items, cartId]);

  // Add a gift card to cart
  const addGiftCardToCart = useCallback(async (gc) => {
    const newItemData = {
      cart_id: cartId,
      type: 'giftcard',
      gift_card_mode: gc.mode,
      experience_type: gc.experienceType || '',
      delivery_method: gc.deliveryMethod,
      send_to_self: !!gc.sendToSelf,
      recipient_name: gc.recipientName || '',
      recipient_email: gc.recipientEmail || '',
      recipient_address_line1: gc.recipientAddressLine1 || '',
      recipient_address_line2: gc.recipientAddressLine2 || '',
      recipient_city: gc.recipientCity || '',
      recipient_postcode: gc.recipientPostcode || '',
      gift_message: gc.giftMessage || '',
      product_name: gc.productName,
      price: gc.price,
      quantity: 1,
    };

    if (cartId) {
      const created = await base44.entities.CartItem.create(newItemData);
      setItems(prev => [...prev, mapCartItem(created)]);
    } else {
      const tempId = 'gc-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
      const newItems = [...items, { ...newItemData, id: tempId, cartItemId: tempId }];
      setItems(newItems);
      saveGuestCart(newItems);
    }
  }, [items, cartId]);

  const updateQuantity = useCallback(async (productId, qty) => {
    if (qty < 1) { removeItem(productId); return; }
    const item = items.find(i => i.product_id === productId);
    if (!item) return;
    if (cartId && item.cartItemId) {
      await base44.entities.CartItem.update(item.cartItemId, { quantity: qty });
    }
    const newItems = items.map(i => i.product_id === productId ? { ...i, quantity: qty } : i);
    setItems(newItems);
    if (!cartId) saveGuestCart(newItems);
  }, [items, cartId]);

  const removeItem = useCallback(async (itemKey) => {
    // itemKey can be product_id (products) or cartItemId (bookings)
    const item = items.find(i => i.cartItemId === itemKey || i.product_id === itemKey);
    if (!item) return;
    if (cartId && item.cartItemId) {
      await base44.entities.CartItem.delete(item.cartItemId);
    }
    const newItems = items.filter(i => i !== item);
    setItems(newItems);
    if (!cartId) saveGuestCart(newItems);
  }, [items, cartId]);

  const clearCart = useCallback(async () => {
    if (cartId) {
      for (const item of items) {
        if (item.cartItemId) await base44.entities.CartItem.delete(item.cartItemId);
      }
      await base44.entities.Cart.update(cartId, { status: 'ordered' });
    }
    localStorage.removeItem(GUEST_CART_KEY);
    setItems([]);
    setCartId(null);
  }, [items, cartId]);

  const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
  const totalPrice = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const hasOnlyBookings = items.length > 0 && items.every(i => i.type === 'booking');
  const hasProducts = items.some(i => i.type === 'product');
  const isPickup = fulfilmentMethod === 'pickup';
  // requiresShipping gates the address section. For pickup we still collect a billing address
  // (for card 3DS), so keep it true whenever there are product items; shipping cost is handled
  // separately via chargeableShipping.
  const requiresShipping = hasProducts;
  const chargeableShipping = hasProducts && !isPickup;

  return (
    <CartContext.Provider value={{ items, cartId, userEmail, loading, addToCart, addBookingToCart, addGiftCardToCart, updateQuantity, removeItem, clearCart, totalItems, totalPrice, hasOnlyBookings, requiresShipping, chargeableShipping, hasProducts, fulfilmentMethod, setFulfilmentMethod, isPickup }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}